import { useCallback, useEffect, useRef, useState } from 'react';
import * as api from './governmentApi';
import type {
  ApplyResult,
  ApplyUpdateInput,
  GovNodeView,
  GovOfficeHolder,
  GovUpdateView,
  HeadChoice,
  NodePatch,
  NodePlan,

  RunView,
  StartRunInput,
  StructureView,
  UpdateQuery,
} from './types';

const message = (error: any, fallback: string): string =>
  error?.response?.data?.message
    ? Array.isArray(error.response.data.message)
      ? error.response.data.message.join(', ')
      : String(error.response.data.message)
    : (error?.message ?? fallback);

/** While something is running, ask often; otherwise leave the server alone */
const ACTIVE_POLL_MS = 1800;
/** The queue changes less often than a log does */
const QUEUE_POLL_MS = 4000;

/** Accepted and not finished: either working, or waiting its turn */
export const isLive = (run: RunView | null | undefined): run is RunView =>
  run?.status === 'running' || run?.status === 'queued';

/**
 * The state of a research run, kept fresh while it works.
 *
 * Runs outlive the page: whoever opens it picks up the run in progress,
 * log and all, rather than starting a second one.
 */
export function useRunWatcher(initial: RunView | null) {
  const [run, setRun] = useState<RunView | null>(initial);
  const [finishedAt, setFinishedAt] = useState<RunView | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cursor = useRef(0);

  const watch = useCallback((next: RunView | null) => {
    cursor.current = next?.logSize ?? 0;
    setRun(next);
    if (next && !isLive(next)) setFinishedAt(next);
  }, []);

  useEffect(() => {
    if (!isLive(run)) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const fresh = await api.fetchRun(run.id, cursor.current);
        if (cancelled) return;
        cursor.current = fresh.logSize;
        setRun((current) => {
          if (!current || current.id !== fresh.id) return current;
          return { ...fresh, log: [...current.log, ...fresh.log].slice(-400) };
        });
        if (!isLive(fresh)) setFinishedAt(fresh);
      } catch {
        // A poll that fails is not worth showing; the next one usually works
      }
      if (!cancelled) timer.current = setTimeout(poll, ACTIVE_POLL_MS);
    };
    timer.current = setTimeout(poll, ACTIVE_POLL_MS);
    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [run]);

  const stop = useCallback(async () => {
    if (!run) return;
    try {
      await api.cancelRun(run.id);
    } catch {
      /* it may have finished on its own */
    }
  }, [run]);

  const dismiss = useCallback(() => {
    setRun(null);
    setFinishedAt(null);
  }, []);

  return { run, watch, stop, dismiss, justFinished: finishedAt };
}

/** While the page is in view, how often it asks whether anything has changed */
const LIVE_POLL_MS = 8000;
/** How long an entity stays marked as having just changed */
const CHANGED_MARK_MS = 6000;

/**
 * What a row of the structure shows, reduced to one string: when it moves
 * between two loads, that entity changed — its name, its record, who holds
 * its post, what is waiting on it — and the page says so.
 */
const signatureOf = (node: GovNodeView, holders: GovOfficeHolder[], waiting: number): string =>
  [
    node.name,
    node.status,
    node.stale ? 'stale' : '',
    node.ended?.how ?? '',
    node.parentId ?? '',
    node.organization?.label ?? '',
    node.organization?.parentId ?? '',
    node.head?.name ?? '',
    node.decision.action,
    (node.decision.mergeIds ?? []).length,
    holders.map((holder) => `${holder.stakeholderId}:${holder.post}:${holder.since ?? ''}`).join(','),
    waiting,
  ].join('|');

/**
 * Cards someone has just applied or dismissed stay where they were, showing
 * what happened, instead of vanishing from under the pointer — until the
 * filter changes.
 */
const withReceipts = (
  fresh: GovUpdateView[],
  previous: GovUpdateView[],
  receipts: Map<string, GovUpdateView>,
): GovUpdateView[] => {
  if (!receipts.size) return fresh;
  const out = fresh.filter((update) => !receipts.has(update.id));
  previous.forEach((update, index) => {
    const receipt = receipts.get(update.id);
    if (receipt) out.splice(Math.min(index, out.length), 0, receipt);
  });
  return out;
};

const chunked = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  return chunks;
};

/**
 * The structure, who holds its posts, the queue and the country in one
 * place — and kept live. Whatever changes them, from anywhere, reaches the
 * page within seconds: it asks the server for a fingerprint of the lot and
 * reloads only when that moves.
 */
export function useGovernment() {
  const [overview, setOverview] = useState<api.Overview | null>(null);
  const [structure, setStructure] = useState<StructureView | null>(null);
  const [holders, setHolders] = useState<GovOfficeHolder[]>([]);
  const [pending, setPending] = useState<GovUpdateView[]>([]);
  const [updates, setUpdates] = useState<GovUpdateView[]>([]);
  const [updateQuery, setQueryState] = useState<UpdateQuery>({ status: 'pending' });
  const [receipts, setReceipts] = useState<Map<string, GovUpdateView>>(() => new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState<RunView[]>([]);
  const [revision, setRevision] = useState(0);
  const [syncedAt, setSyncedAt] = useState<number | null>(null);
  const [recentlyChanged, setRecentlyChanged] = useState<Set<string>>(() => new Set());
  const watcher = useRunWatcher(null);
  const { watch, run } = watcher;
  const running = run?.status === 'running';
  const wasRunning = useRef(false);
  const signatures = useRef<Map<string, string> | null>(null);
  const stamp = useRef<string | null>(null);
  const markTimers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const receiptsRef = useRef(receipts);
  receiptsRef.current = receipts;
  const queryRef = useRef(updateQuery);
  queryRef.current = updateQuery;

  useEffect(
    () => () => {
      markTimers.current.forEach(clearTimeout);
    },
    [],
  );

  const mark = useCallback((ids: string[]) => {
    if (!ids.length) return;
    setRecentlyChanged((current) => new Set([...Array.from(current), ...ids]));
    markTimers.current.push(
      setTimeout(() => {
        setRecentlyChanged((current) => {
          const next = new Set(current);
          ids.forEach((id) => next.delete(id));
          return next;
        });
      }, CHANGED_MARK_MS),
    );
  }, []);

  /**
   * What is waiting behind the run in progress. Research of one kind runs one
   * at a time, so a second request joins a queue rather than being refused,
   * and the page has to be able to show and unpick that queue.
   */
  const refreshQueue = useCallback(async () => {
    try {
      const runs = await api.fetchRuns(20);
      setQueue(runs.filter((item) => item.status === 'queued'));
      return runs;
    } catch {
      return [] as RunView[];
    }
  }, []);

  const loadStructure = useCallback(async () => {
    const [view, counts, people, waiting] = await Promise.all([
      api.fetchStructure(),
      api.fetchUpdateCounts(),
      api.fetchOfficeHolders().catch(() => null),
      api.fetchUpdates({ status: 'pending' }).catch(() => null),
    ]);
    const byOrg = new Map<string, GovOfficeHolder[]>();
    for (const holder of people ?? []) {
      byOrg.set(holder.organizationId, [...(byOrg.get(holder.organizationId) ?? []), holder]);
    }
    const waitingByOrg = new Map<string, number>();
    for (const update of waiting ?? []) {
      if (update.organization.id) {
        waitingByOrg.set(update.organization.id, (waitingByOrg.get(update.organization.id) ?? 0) + 1);
      }
    }
    const next = new Map(
      view.nodes.map((node) => [
        node.id,
        signatureOf(
          node,
          node.organizationId ? (byOrg.get(node.organizationId) ?? []) : [],
          node.organizationId ? (waitingByOrg.get(node.organizationId) ?? 0) : 0,
        ),
      ]),
    );
    const previous = signatures.current;
    signatures.current = next;
    if (previous) {
      const changed: string[] = [];
      next.forEach((signature, id) => {
        if (previous.get(id) !== signature) changed.push(id);
      });
      // A change to nearly everything at once is a new survey, not news
      // about particular entities
      if (changed.length <= Math.max(12, next.size / 3)) mark(changed);
    }

    setStructure(view);
    if (people) setHolders(people);
    if (waiting) setPending(waiting);
    setOverview((current) =>
      current
        ? { ...current, country: view.country, structure: view.stats, updates: counts, matchedAt: view.matchedAt }
        : current,
    );
    setSyncedAt(Date.now());
    setRevision((value) => value + 1);
    return view;
  }, [mark]);

  const loadUpdates = useCallback(async (query: UpdateQuery) => {
    const fresh = await api.fetchUpdates(query);
    setUpdates((previous) => withReceipts(fresh, previous, receiptsRef.current));
  }, []);

  /** Everything on the page, read again, with the fingerprint it was read at */
  const refreshAll = useCallback(async () => {
    const seen = await api.fetchChanges().catch(() => null);
    await Promise.all([
      loadStructure().catch(() => undefined),
      loadUpdates(queryRef.current).catch(() => undefined),
    ]);
    // Taken before reading, so anything that changes while the page reads is
    // caught by the next check rather than missed
    if (seen) stamp.current = seen.stamp;
  }, [loadStructure, loadUpdates]);

  const reload = useCallback(async () => {
    setError(null);
    try {
      const seen = await api.fetchChanges().catch(() => null);
      const [summary] = await Promise.all([
        api.fetchOverview(),
        loadStructure(),
        loadUpdates(queryRef.current),
      ]);
      if (seen) stamp.current = seen.stamp;
      setOverview(summary);
      if (summary.activeRun) watch(summary.activeRun);
    } catch (failure: any) {
      setError(
        failure instanceof api.GovernmentUnavailableError
          ? failure.message
          : message(failure, 'The government map could not be loaded'),
      );
    } finally {
      setLoading(false);
    }
  }, [loadStructure, loadUpdates, watch]);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Changing the filter lets go of the cards kept from the last one */
  const setUpdateQuery = useCallback((next: UpdateQuery | ((current: UpdateQuery) => UpdateQuery)) => {
    setReceipts(new Map());
    receiptsRef.current = new Map();
    setQueryState(next);
  }, []);

  const firstQuery = useRef(true);
  useEffect(() => {
    // The first list comes with the first load
    if (firstQuery.current) {
      firstQuery.current = false;
      return;
    }
    void loadUpdates(updateQuery).catch(() => undefined);
  }, [updateQuery, loadUpdates]);

  // Live: while the page is in view, ask whether anything has changed and
  // reload when it has; look again straight away when the tab comes back
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const check = async () => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') {
        try {
          const { stamp: current } = await api.fetchChanges();
          if (!cancelled && stamp.current !== null && current !== stamp.current) {
            await refreshAll();
          } else if (!cancelled && stamp.current === null) {
            stamp.current = current;
          }
        } catch {
          // An older server without the fingerprint, or a blip: try later
        }
      }
      if (!cancelled) timer = setTimeout(check, LIVE_POLL_MS);
    };
    timer = setTimeout(check, LIVE_POLL_MS);
    const wake = () => {
      if (document.visibilityState !== 'visible') return;
      if (timer) clearTimeout(timer);
      void check();
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('focus', wake);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('focus', wake);
    };
  }, [refreshAll]);

  // A run that has just finished has usually changed everything on the page
  useEffect(() => {
    if (running) {
      wasRunning.current = true;
      return;
    }
    if (!wasRunning.current) return;
    wasRunning.current = false;
    void refreshAll();
    void api
      .fetchOverview()
      .then((summary) => setOverview((current) => ({ ...summary, activeRun: current?.activeRun ?? null })))
      .catch(() => undefined);
  }, [running, refreshAll]);

  // While anything is live, keep the queue fresh and move the console on to
  // whatever starts next, so the page always shows the run that is working
  useEffect(() => {
    if (!isLive(run) && queue.length === 0) return;
    let cancelled = false;
    const timer = setInterval(() => {
      void refreshQueue().then((runs) => {
        if (cancelled || !runs.length) return;
        const working = runs.find((item) => item.status === 'running');
        if (working && working.id !== run?.id && !isLive(run)) watch(working);
      });
    }, QUEUE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [run, queue.length, refreshQueue, watch]);

  const guard = useCallback(async <T,>(work: () => Promise<T>, fallback: string): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try {
      return await work();
    } catch (failure: any) {
      setError(message(failure, fallback));
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  const start = useCallback(
    async (input: StartRunInput) => {
      const started = await guard(() => api.startRun(input), 'The research could not be started');
      if (!started) return started;
      // A run that has to wait goes on the queue strip; the console stays on
      // whatever is actually working
      if (started.status === 'queued') await refreshQueue();
      else watch(started);
      return started;
    },
    [guard, watch, refreshQueue],
  );

  /** Pick a stopped run back up, rather than starting it over */
  const resume = useCallback(
    async (id: string) => {
      const started = await guard(() => api.resumeRun(id), 'That research could not be resumed');
      if (!started) return started;
      if (started.status === 'queued') await refreshQueue();
      else watch(started);
      return started;
    },
    [guard, watch, refreshQueue],
  );

  /** Stop a run, or take one off the queue before it starts */
  const cancelRun = useCallback(
    async (id: string) => {
      try {
        await api.cancelRun(id);
      } catch {
        // It may have started or finished in the meantime
      }
      await refreshQueue();
    },
    [refreshQueue],
  );

  const replaceNode = useCallback((node: GovNodeView) => {
    setStructure((current) =>
      current
        ? { ...current, nodes: current.nodes.map((item) => (item.id === node.id ? node : item)) }
        : current,
    );
  }, []);

  const patch = useCallback(
    async (id: string, changes: NodePatch) => {
      const node = await guard(() => api.patchNode(id, changes), 'That change could not be saved');
      if (node) {
        replaceNode(node);
        void refreshAll();
      }
      return node;
    },
    [guard, replaceNode, refreshAll],
  );

  const setStatus = useCallback(
    async (ids: string[], status: 'proposed' | 'confirmed' | 'rejected') => {
      const done = await guard(() => api.setNodeStatus(ids, status), 'That change could not be saved');
      if (done) await refreshAll();
      return done;
    },
    [guard, refreshAll],
  );

  /** What applying these would do, before anything is written */
  const plan = useCallback(async (ids: string[]): Promise<NodePlan[]> => {
    const plans: NodePlan[] = [];
    for (const chunk of chunked(ids, 100)) {
      const { plans: part } = await api.planNodes(chunk);
      plans.push(...part);
    }
    return plans;
  }, []);

  const apply = useCallback(
    async (
      ids: string[],
      options: {
        heads?: Record<string, HeadChoice>;
        onProgress?: (done: number, total: number) => void;
      } = {},
    ): Promise<ApplyResult[]> => {
      const results: ApplyResult[] = [];
      setBusy(true);
      setError(null);
      try {
        for (const chunk of chunked(ids, 10)) {
          const heads = options.heads
            ? Object.fromEntries(chunk.filter((id) => options.heads![id]).map((id) => [id, options.heads![id]]))
            : undefined;
          const { results: part } = await api.applyNodes(chunk, heads);
          results.push(...part);
          options.onProgress?.(results.length, ids.length);
        }
      } catch (failure: any) {
        setError(message(failure, 'Applying stopped partway through'));
      } finally {
        setBusy(false);
        await refreshAll();
      }
      return results;
    },
    [refreshAll],
  );

  const addNode = useCallback(
    async (input: Parameters<typeof api.addNode>[0]) => {
      const node = await guard(() => api.addNode(input), 'That entity could not be added');
      if (node) await refreshAll();
      return node;
    },
    [guard, refreshAll],
  );

  const resolveOutside = useCallback(
    async (organizationId: string, action: 'detach' | 'move' | 'keep', targetNodeId?: string | null) => {
      const done = await guard(
        () => api.resolveOutside(organizationId, action, targetNodeId),
        'That record could not be changed',
      );
      if (done) await refreshAll();
      return done;
    },
    [guard, refreshAll],
  );

  /** A reviewed card, kept in place with what happened to it */
  const keepReceipt = useCallback((updated: GovUpdateView) => {
    setReceipts((current) => {
      const next = new Map(current);
      next.set(updated.id, updated);
      receiptsRef.current = next;
      return next;
    });
    setUpdates((list) => list.map((item) => (item.id === updated.id ? updated : item)));
  }, []);

  const applyUpdate = useCallback(
    async (id: string, input: ApplyUpdateInput = {}) => {
      const updated = await guard(() => api.applyUpdate(id, input), 'That change could not be applied');
      if (updated) {
        keepReceipt(updated);
        void refreshAll();
      }
      return updated;
    },
    [guard, keepReceipt, refreshAll],
  );

  /** Several changes applied one after another, the page read once after */
  const applyUpdates = useCallback(
    async (ids: string[]) =>
      guard(async () => {
        let applied = 0;
        let failed = 0;
        for (const id of ids) {
          try {
            await api.applyUpdate(id, {});
            applied++;
          } catch {
            failed++;
          }
        }
        void refreshAll();
        return { applied, failed };
      }, 'Those changes could not be applied'),
    [guard, refreshAll],
  );

  const dismissUpdate = useCallback(
    async (id: string, reason?: string) => {
      const updated = await guard(() => api.dismissUpdate(id, reason), 'That change could not be dismissed');
      if (updated) {
        keepReceipt(updated);
        void refreshAll();
      }
      return updated;
    },
    [guard, keepReceipt, refreshAll],
  );

  const verifyUpdate = useCallback(
    async (id: string) => {
      const started = await guard(() => api.verifyUpdate(id), 'The check could not be started');
      if (!started) return started;
      if (started.status === 'queued') await refreshQueue();
      else watch(started);
      return started;
    },
    [guard, watch, refreshQueue],
  );

  const setCountry = useCallback(
    async (input: Parameters<typeof api.saveCountry>[0]) => {
      const saved = await guard(() => api.saveCountry(input), 'The country could not be saved');
      if (saved) await reload();
      return saved;
    },
    [guard, reload],
  );

  return {
    loading,
    busy,
    error,
    setError,
    overview,
    structure,
    /** Who the directory records in post at each mapped body */
    holders,
    /** Every change waiting for review, whatever the Updates tab shows */
    pending,
    /** Cards reviewed on this page, with what happened to them */
    receipts,
    updates,
    updateQuery,
    setUpdateQuery,
    watcher,
    queue,
    cancelRun,
    resume,
    /** Research of this kind is working or waiting, so a new request joins the queue */
    queueing: isLive(run) || queue.length > 0,
    /** Goes up every time the page has read the server again */
    revision,
    /** When the page last read the server */
    syncedAt,
    /** Entities that changed at the last reload, for a few seconds */
    recentlyChanged,
    reload,
    refreshAll,
    start,
    patch,
    setStatus,
    plan,
    apply,
    addNode,
    resolveOutside,
    applyUpdate,
    applyUpdates,
    dismissUpdate,
    verifyUpdate,
    setCountry,
  };
}

/**
 * What has been done at one body: the changes applied there, newest first.
 * Read again whenever the page does, so a change applied a moment ago is
 * already in it.
 */
export function useEntityHistory(organizationId: string | null | undefined, revision: number) {
  const [history, setHistory] = useState<GovUpdateView[]>([]);

  useEffect(() => {
    if (!organizationId) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    api
      .fetchUpdates({ organizationId, status: 'applied', limit: 30 })
      .then((rows) => !cancelled && setHistory(rows))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [organizationId, revision]);

  return history;
}

/** The pending suggestions about one person, for their own page */
export function useStakeholderUpdates(stakeholderId?: string) {
  const [updates, setUpdates] = useState<GovUpdateView[]>([]);
  const [reviewed, setReviewed] = useState<GovUpdateView[]>([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!stakeholderId) return;
    try {
      setUpdates(await api.fetchUpdates({ stakeholderId }));
    } catch {
      setUpdates([]);
    }
  }, [stakeholderId]);

  useEffect(() => {
    setReviewed([]);
    void load();
  }, [load]);

  const act = useCallback(
    async (
      id: string,
      action: 'apply' | 'dismiss',
      input: ApplyUpdateInput & { reason?: string } = {},
    ) => {
      setBusy(true);
      try {
        const done =
          action === 'apply' ? await api.applyUpdate(id, input) : await api.dismissUpdate(id, input.reason);
        setReviewed((current) => [...current.filter((item) => item.id !== done.id), done]);
        await load();
        return null;
      } catch (error: any) {
        return message(error, 'That change could not be applied');
      } finally {
        setBusy(false);
      }
    },
    [load],
  );

  // What was just reviewed stays on the page, in place, saying what happened
  const shown = [
    ...updates.filter((update) => !reviewed.some((item) => item.id === update.id)),
    ...reviewed,
  ];

  return { updates: shown, busy, act, reload: load };
}
